import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { getAllBranch } from "../../services";
import { Branch } from "../../types/user.type";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PURCHASE_ORDER_NO_BRANCH_FILTER,
  PURCHASE_ORDER_STATUS_LABEL,
} from "../../types/purchase-order.type";
import DateRangePicker from "../common/DateRangePicker";
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
} from "../SaleDepositReport/FilterField";
import {
  PO_DELIVERY_PRESET_GROUPS,
  PO_DELIVERY_RANGE_FORWARD_DAYS,
  PO_UNFILTERED,
  PURCHASE_ORDER_CHAIN_ORDER,
  PURCHASE_ORDER_STATUS_ORDER,
} from "./data";

interface PurchaseOrderFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  chain: string;
  onChangeChain: (chain: string) => void;
  status: string;
  onChangeStatus: (status: string) => void;
  search: string;
  onChangeSearch: (search: string) => void;
  /**
   * ช่องเลือกสาขา มีเฉพาะจอของ HO · ผู้ใช้สาขาถูก backend ปักไว้ที่สาขาตัวเองอยู่แล้ว
   * การไม่แสดงช่องนี้จึงเป็นความสะดวก ไม่ใช่การกั้นสิทธิ์
   */
  branchFilter?: PurchaseOrderBranchFilter;
}

export interface PurchaseOrderBranchFilter {
  companyId: string;
  onChange: (companyId: string) => void;
}

/**
 * แถบตัวกรองของรายการ PO ใช้ร่วมกันระหว่าง W1 (รายการ PO ของ HO) และ W7 (PO ของสาขา)
 * เพื่อให้ทั้งสองจอกรองด้วยเงื่อนไขชุดเดียวกัน — ไฟล์ Export ก็ใช้ตัวกรองชุดนี้
 */
export default function PurchaseOrderFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  chain,
  onChangeChain,
  status,
  onChangeStatus,
  search,
  onChangeSearch,
  branchFilter,
}: PurchaseOrderFilterProps) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const canSelectBranch = Boolean(branchFilter);

  const loadBranches = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data ?? []);
  }, []);

  useEffect(() => {
    if (canSelectBranch) loadBranches();
  }, [canSelectBranch, loadBranches]);

  // PO มาถึงก่อนวันส่ง ช่วงวันที่จึงต้องเลือกวันข้างหน้าได้ ไม่ใช่หยุดแค่วันนี้
  const maxDeliveryDate = useMemo(
    () => dayjs().startOf("day").add(PO_DELIVERY_RANGE_FORWARD_DAYS, "day"),
    [],
  );

  const handleChangeChain = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeChain(event.target.value);
  };

  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeStatus(event.target.value);
  };

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeSearch(event.target.value);
  };

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    if (branchFilter) branchFilter.onChange(event.target.value);
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันส่ง">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
          maxDate={maxDeliveryDate}
          presetGroups={PO_DELIVERY_PRESET_GROUPS}
        />
      </FilterField>

      {branchFilter && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branchFilter.companyId}
            onChange={handleChangeBranch}
          >
            <MenuItem value={PO_UNFILTERED}>ทุกสาขา</MenuItem>
            {/* company_id=none — ใบที่ HO ยังไม่ได้ชี้สาขา (บันทึกจากไฟล์นำเข้าไว้ก่อน) */}
            <MenuItem value={PURCHASE_ORDER_NO_BRANCH_FILTER}>
              {PURCHASE_ORDER_STATUS_LABEL.NO_BRANCH}
            </MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.company_id}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      <FilterField label="เชน">
        <TextField
          select
          size="small"
          fullWidth
          value={chain}
          onChange={handleChangeChain}
        >
          <MenuItem value={PO_UNFILTERED}>ทุกเชน</MenuItem>
          {PURCHASE_ORDER_CHAIN_ORDER.map((option) => (
            <MenuItem key={option} value={option}>
              {PURCHASE_ORDER_CHAIN_LABEL[option]}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
        >
          <MenuItem value={PO_UNFILTERED}>ทุกสถานะ</MenuItem>
          {PURCHASE_ORDER_STATUS_ORDER.map((option) => (
            <MenuItem key={option} value={option}>
              {PURCHASE_ORDER_STATUS_LABEL[option]}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      <FilterField label="ค้นหา">
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={handleChangeSearch}
          placeholder="เลข PO / รหัสร้าน / ชื่อร้าน"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </FilterField>
    </Grid>
  );
}
